/**
 * Pine Script Storage - Save and manage user scripts
 */

export interface SavedScript {
  id: string;
  name: string;
  description?: string;
  code: string;
  createdAt: string;
  updatedAt: string;
  isRobot: boolean; // true = auto-trading robot, false = indicator only
  settings?: {
    symbol?: string;
    timeframe?: string;
    platform?: string;
    isActive?: boolean;
  };
}

export interface PineStorage {
  scripts: SavedScript[];
  activeRobots: string[]; // IDs of currently running robots
}

const STORAGE_KEY = 'kit_pine_scripts';

export function loadPineStorage(): PineStorage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load pine storage:', e);
  }
  
  return {
    scripts: [],
    activeRobots: [],
  };
}

export function savePineStorage(storage: PineStorage): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
  } catch (e) {
    console.error('Failed to save pine storage:', e);
  }
}

export function saveScript(storage: PineStorage, script: Omit<SavedScript, 'id' | 'createdAt' | 'updatedAt'>): PineStorage {
  const now = new Date().toISOString();
  const newScript: SavedScript = {
    ...script,
    id: Date.now().toString(),
    createdAt: now,
    updatedAt: now,
  };
  
  return {
    ...storage,
    scripts: [...storage.scripts, newScript],
  };
}

export function updateScript(storage: PineStorage, id: string, updates: Partial<SavedScript>): PineStorage {
  return {
    ...storage,
    scripts: storage.scripts.map(s => 
      s.id === id 
        ? { ...s, ...updates, updatedAt: new Date().toISOString() }
        : s
    ),
  };
}

export function deleteScript(storage: PineStorage, id: string): PineStorage {
  return {
    ...storage,
    scripts: storage.scripts.filter(s => s.id !== id),
    activeRobots: storage.activeRobots.filter(rid => rid !== id),
  };
}

export function toggleRobot(storage: PineStorage, id: string): PineStorage {
  const isActive = storage.activeRobots.includes(id);
  return {
    ...storage,
    activeRobots: isActive
      ? storage.activeRobots.filter(rid => rid !== id)
      : [...storage.activeRobots, id],
  };
}

export function exportScripts(scripts: SavedScript[]): string {
  return JSON.stringify(scripts, null, 2);
}

export function importScripts(json: string): SavedScript[] {
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) {
      return parsed.map((s, i) => ({
        id: Date.now().toString() + i,
        name: s.name || 'Imported Script',
        code: s.code || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isRobot: s.isRobot || false,
        description: s.description,
        settings: s.settings,
      }));
    }
  } catch (e) {
    console.error('Failed to import scripts:', e);
  }
  return [];
}
